import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isMusicOn, onMusicChange, playDjReverse } from "../utils/sound";
import { routeKey } from "../utils/perf";

// The owner's soundtrack crate — a different song on every page:
//  Home       -> 90's Chill Lofi / Study Beats (swapped from Repair Lab)
//  Repair Lab -> HOME – Resonance (Slowed + Reverb) (swapped from Home)
//  Phones     -> Lofi Girl – lofi hip hop mix Part.1 (owner asked for a change)
//  Showroom   -> Best of Nujabes (classic jazzy hip hop)
//  Visit      -> Nujabes jazz hip-hop mix Part.1 (has a beat)
//  Quote      -> Vinyl Jazz Hiphop mix
// When a song finishes the deck SHUFFLES to a different track, always with a
// DJ reverse scratch + crossfade — the music never stops unless muted.
const HOME_TRACK = "sF80I-TQiW0";
const RESONANCE = "PKslgn07kLo";
// Resonance rides last in the crate, per the owner.
const PLAYLIST = ["CFGLoQIhmow", HOME_TRACK, "xMVaEnw5kpk", "TWbIXIuuCGU", "UvCQc2wrqx4", "8b3fqIBrNW0", RESONANCE];
const TRACKS_BY_ROUTE = {
  "/": HOME_TRACK,
  "/repair-lab": RESONANCE,
  "/phones": "CFGLoQIhmow",
  "/showroom": "xMVaEnw5kpk",
  "/visit": "TWbIXIuuCGU",
  "/quote": "UvCQc2wrqx4",
};
const DEFAULT_TRACK = "TWbIXIuuCGU";
const CONTAINER_ID = "nextg-bg-music";
const VOLUME = 38;

// Hidden YouTube player streaming the site's background songs, looping each
// forever. Route changes DJ-crossfade into the next track with a reverse
// scratch. Browsers block audio until a user gesture, so the first
// tap/click/keypress anywhere unlocks playback.
export const BackgroundMusic = () => {
  const player = useRef(null);
  const currentTrack = useRef(null);
  const fading = useRef(null);
  const switchTrackRef = useRef(() => {});
  const errStreak = useRef(0);
  const { pathname } = useLocation();

  // Smooth volume ramp — the DJ crossfade between page songs.
  const fadeTo = (target, ms, done) => {
    if (fading.current) clearInterval(fading.current);
    const p = player.current;
    if (!p || typeof p.getVolume !== "function") {
      if (done) done();
      return;
    }
    let vol;
    try {
      vol = p.getVolume();
    } catch {
      vol = target;
    }
    const steps = 12;
    const step = (target - vol) / steps;
    let i = 0;
    fading.current = setInterval(() => {
      i += 1;
      vol = i >= steps ? target : vol + step;
      try {
        p.setVolume(Math.max(0, Math.min(100, Math.round(vol))));
      } catch {
        /* player gone */
      }
      if (i >= steps) {
        clearInterval(fading.current);
        fading.current = null;
        if (done) done();
      }
    }, ms / steps);
  };

  const switchTrack = (videoId, { dj = false } = {}) => {
    const p = player.current;
    if (!p || typeof p.loadVideoById !== "function") {
      currentTrack.current = videoId; // consumed onReady
      return;
    }
    if (currentTrack.current === videoId) return;
    currentTrack.current = videoId;
    // DJ reverse scratch only between songs (shuffle), never on page turns.
    if (dj && isMusicOn()) playDjReverse();
    fadeTo(0, 450, () => {
      try {
        if (isMusicOn()) {
          p.loadVideoById(videoId);
          p.setVolume(0);
          fadeTo(VOLUME, 650);
        } else {
          p.cueVideoById(videoId);
          p.setVolume(VOLUME);
        }
      } catch {
        /* retry happens on next gesture/route change */
      }
    });
  };
  switchTrackRef.current = switchTrack;
  useEffect(() => {
    // DJ scratch signals every song switch — including page-to-page.
    switchTrackRef.current(TRACKS_BY_ROUTE[routeKey(pathname)] || DEFAULT_TRACK, { dj: true });
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;

    const apply = () => {
      const p = player.current;
      if (!p || typeof p.playVideo !== "function") return;
      try {
        if (isMusicOn()) {
          p.setVolume(VOLUME);
          p.playVideo();
        } else {
          p.pauseVideo();
        }
      } catch {
        /* player not ready yet — next gesture retries */
      }
    };

    const create = () => {
      if (cancelled || player.current || !window.YT || !window.YT.Player) return;
      const startTrack = currentTrack.current || TRACKS_BY_ROUTE[routeKey(window.location.pathname)] || DEFAULT_TRACK;
      currentTrack.current = startTrack;
      player.current = new window.YT.Player(CONTAINER_ID, {
        videoId: startTrack,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: (e) => {
            // If the route changed while the API was loading, catch up.
            const wanted = currentTrack.current;
            if (wanted && wanted !== startTrack) {
              try {
                e.target.cueVideoById(wanted);
              } catch {
                /* ignore */
              }
            }
            apply();
          },
          onStateChange: (e) => {
            if (!window.YT) return;
            if (e.data === window.YT.PlayerState.PLAYING) {
              errStreak.current = 0; // healthy playback resets the breaker
              return;
            }
            // Watchdog: if playback stalls/pauses while music is on (network
            // hiccup, livestream stall), resume it — the music never cuts off.
            if (e.data === window.YT.PlayerState.PAUSED && isMusicOn()) {
              setTimeout(() => {
                const p = player.current;
                if (p && isMusicOn()) {
                  try {
                    p.playVideo();
                  } catch {
                    /* next state change retries */
                  }
                }
              }, 900);
              return;
            }
            // Song finished — shuffle to a different track from the crate.
            if (e.data === window.YT.PlayerState.ENDED && isMusicOn()) {
              const others = PLAYLIST.filter((id) => id !== currentTrack.current);
              const next = others[Math.floor(Math.random() * others.length)];
              switchTrackRef.current(next, { dj: true });
            }
          },
          onError: () => {
            // A track failed (blocked embedding etc.) — skip to another one,
            // but trip a circuit breaker so repeated failures (e.g. offline)
            // never spin into a retry storm that lags the page.
            if (!isMusicOn()) return;
            errStreak.current += 1;
            if (errStreak.current > 3) {
              // Breaker tripped — rest, then try once more after a cool-down
              // so temporary network trouble doesn't silence the site.
              setTimeout(() => {
                errStreak.current = 0;
                if (isMusicOn() && player.current && currentTrack.current) {
                  try {
                    player.current.loadVideoById(currentTrack.current);
                  } catch {
                    /* next gesture retries */
                  }
                }
              }, 25000);
              return;
            }
            const others = PLAYLIST.filter((id) => id !== currentTrack.current);
            const next = others[Math.floor(Math.random() * others.length)];
            setTimeout(() => {
              if (!isMusicOn() || !player.current) return;
              try {
                currentTrack.current = next;
                player.current.loadVideoById(next);
              } catch {
                /* retry happens on next gesture */
              }
            }, 1500);
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      create();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        create();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.async = true;
        document.head.appendChild(tag);
      }
    }

    // First user gesture anywhere unlocks audio (browser autoplay policy).
    // Also resets the error circuit breaker if it tripped.
    const unlock = () => {
      if (errStreak.current > 3 && isMusicOn() && player.current && currentTrack.current) {
        errStreak.current = 0;
        try {
          player.current.loadVideoById(currentTrack.current);
        } catch {
          /* ignore */
        }
      }
      apply();
    };
    window.addEventListener("pointerdown", unlock, { passive: true });
    window.addEventListener("keydown", unlock);
    const unsub = onMusicChange(apply);

    return () => {
      cancelled = true;
      unsub();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      if (fading.current) clearInterval(fading.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1px transparent but rendered — display:none would make some browsers
  // suspend the audio.
  return (
    <div
      data-testid="bg-music-player"
      aria-hidden="true"
      className="pointer-events-none fixed bottom-0 left-0 z-0 h-px w-px overflow-hidden opacity-0"
    >
      <div id={CONTAINER_ID} />
    </div>
  );
};
